import { Dispatch, SetStateAction, useCallback, useSyncExternalStore } from 'react';
import { safeLocalStorage } from '@/utils/storage';

const subscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);

  return () => window.removeEventListener('storage', callback);
};

const parse = <T>(raw: string | null, initialValue: T): T => {
  if (raw === null) return initialValue;

  try {
    return JSON.parse(raw) as T;
  } catch {
    return initialValue;
  }
};

export const useSafeLocalStorage = <T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const store = useSyncExternalStore(
    subscribe,
    () => safeLocalStorage.getItem(key),
    () => null,
  );

  const setValue = useCallback(
    (action: SetStateAction<T>) => {
      const next = action instanceof Function ? action(parse(safeLocalStorage.getItem(key), initialValue)) : action;

      safeLocalStorage.setItem(key, JSON.stringify(next));
      window.dispatchEvent(new StorageEvent('storage', { key }));
    },
    [key, initialValue],
  );

  return [parse(store, initialValue), setValue];
};
